import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  Box,
  LineChart,
  LockedFeatureOverlay,
  QuoteTrend,
  Screen,
  Skeleton,
  Text,
} from '~/atomic';
import type { LineChartPoint } from '~/atomic/molecules/LineChart/interfaces';
import type { QuoteProduct } from '~/store/quotes/interfaces';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  selectDetailLoading,
  selectQuoteDetail,
  selectQuotesUf,
} from '~/store/quotes/selectors';
import { fetchQuoteDetail } from '~/store/quotes/thunks';

const PRODUCT_LABELS: Record<QuoteProduct, string> = {
  soja: 'Soja',
  milho: 'Milho',
  boi_gordo: 'Boi gordo',
  dolar: 'Dólar',
};

export default function QuoteDetailsView() {
  const { product } = useParams<{ product: QuoteProduct }>();
  const dispatch = useAppDispatch();
  const uf = useAppSelector(selectQuotesUf);
  const loading = useAppSelector(selectDetailLoading);
  const detail = useAppSelector(selectQuoteDetail(product as QuoteProduct));

  useEffect(() => {
    if (product) dispatch(fetchQuoteDetail({ product: product as QuoteProduct, uf }));
  }, [dispatch, product, uf]);

  const MONTH_ABR = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];

  const points = useMemo<LineChartPoint[]>(() => {
    if (!detail) return [];
    return detail.history.map((h) => {
      const d = new Date(h.t);
      return {
        label: `${d.getUTCDate().toString().padStart(2, '0')} ${MONTH_ABR[d.getUTCMonth()]}`,
        value: Math.round(h.price * 100),
      };
    });
  }, [detail]);

  if (!detail) {
    return (
      <Screen headerTitle={PRODUCT_LABELS[product as QuoteProduct] ?? 'Cotação'}>
        <div className="flex flex-col gap-smaller py-medium">
          {loading ? <Skeleton height={180} /> : <Text className="text-bodyMD color-intense-06">Sem dados.</Text>}
        </div>
      </Screen>
    );
  }

  return (
    <Screen
      headerTitle={PRODUCT_LABELS[detail.product]}
      headerSubtitle={detail.praca ?? (uf ?? 'Brasil')}
    >
      <div className="flex flex-col gap-medium py-medium">
        <Box className="rounded-medium bg-intense-01 p-medium shadow-level1">
          <Text className="text-captionLG color-intense-06 uppercase">
            Preço atual ({detail.unit})
          </Text>
          <Text className="mt-quark text-display1 color-complement-01">
            {detail.unit === 'BRL'
              ? `R$ ${detail.price.toFixed(2).replace('.', ',')}`
              : `${detail.price.toFixed(2).replace('.', ',')} ${detail.unit}`}
          </Text>
          <QuoteTrend pct={detail.change?.pct ?? null} />
        </Box>

        <LockedFeatureOverlay
          feature="cotacoes_history"
          title="Gráfico histórico"
          description="Veja a variação do preço nos últimos meses e identifique tendências. Disponível nos planos pagos."
        >
          <Box className="rounded-medium bg-intense-01 p-medium border border-intense-03">
            <div className="flex items-center justify-between mb-smaller">
              <Text className="text-paragraphMD color-complement-01">Histórico de preço</Text>
              {points.length > 0 && (
                <Text className="text-captionMD color-intense-06">
                  {`${points[0].label} – ${points[points.length - 1].label}`}
                </Text>
              )}
            </div>
            <LineChart
              points={points}
              height={200}
              showLabels
              showYAxis
              formatValue={(v) => {
                const price = v / 100;
                if (detail.unit === 'BRL')
                  return `R$ ${price.toFixed(2).replace('.', ',')}`;
                return price.toFixed(2).replace('.', ',');
              }}
            />
          </Box>
        </LockedFeatureOverlay>

        {detail.stats ? (
          <Box className="grid grid-cols-3 gap-smaller">
            <Box className="rounded-small bg-intense-01 border border-intense-03 p-small">
              <Text className="text-captionMD color-intense-06">Máxima</Text>
              <Text className="mt-quark text-bodyLG color-success-06">
                {detail.stats.high.toFixed(2)}
              </Text>
            </Box>
            <Box className="rounded-small bg-intense-01 border border-intense-03 p-small">
              <Text className="text-captionMD color-intense-06">Mínima</Text>
              <Text className="mt-quark text-bodyLG color-warning-06">
                {detail.stats.low.toFixed(2)}
              </Text>
            </Box>
            <Box className="rounded-small bg-intense-01 border border-intense-03 p-small">
              <Text className="text-captionMD color-intense-06">Média</Text>
              <Text className="mt-quark text-bodyLG color-complement-01">
                {detail.stats.avg.toFixed(2)}
              </Text>
            </Box>
          </Box>
        ) : null}
      </div>
    </Screen>
  );
}
